import {repeat} from 'lodash/fp'

/**
 * @param maxLength {number} shorten 으로 만들 최대 글자수
 * @param dotSize {number} dotSize
 * @returns {Function}
 * @description 최대 글자수를 받아 글자수를 넘으면, ... 처리를 하는 함수를 반환
 */
export const shortenText = (maxLength: number, dotSize = 3) => {
    if (!(maxLength > 0)) {
        throw new Error('maxLength is not valuable')
    }
    return (text: string) => {
        let result = ''
        if (text) {
            if (text.length > maxLength) {
                const sliceSize = maxLength > dotSize ? maxLength - dotSize : maxLength
                result = `${text.substring(0, sliceSize).trim()}${repeat(dotSize, '.')}`
            } else {
                result = text
            }
        }
        return result
    }
}

type DecorateTextOptions<T> = {
    prefix?: string
    postfix?: string
    shouldPrefix?: boolean
    shouldPostfix?: boolean
    render?: ((v: T) => boolean) | boolean
}

const createDecorateText =
    <T>({prefix = '', postfix = '', shouldPrefix, shouldPostfix, render = true}: DecorateTextOptions<T> = {}) =>
    (value: T): string => {
        const text = `${value}`
        const isRender = () => {
            if (render == null) {
                return true
            }
            if (typeof render === 'boolean') {
                return render
            }
            return render(value)
        }
        if (!(value != null && text && isRender())) {
            return ''
        }
        const shouldDecorate = (v?: boolean): boolean => (typeof v === 'boolean' ? v : true)
        return `${shouldDecorate(shouldPrefix) ? (prefix ?? '') : ''}${text}${
            shouldDecorate(shouldPostfix) ? (postfix ?? '') : ''
        }`
    }

/** Text 에 option 을 받아 채워주는 함수 */
export const decorateText = <T>(text: T, options?: DecorateTextOptions<T>) => {
    return createDecorateText<T>(options)(text)
}

/** Text 배열을 받아서 문자열을 만들어주는 함수 */
export const decorateTexts = <T>(
    texts: (({text?: string | number} & DecorateTextOptions<T>) | string | number | undefined | null)[],
    {
        join = '',
    }: {
        join?: string
    } = {},
) => {
    const getText = (beforeText: string, value: T, option?: DecorateTextOptions<T>) => {
        const text = decorateText<T>(value, option)
        return `${decorateText(beforeText, {
            postfix: join,
            shouldPostfix: !!text,
        })}${text}`
    }
    return texts.reduce<string>((acc, value) => {
        if (value == null) {
            return acc
        }
        if (typeof value === 'number' || typeof value === 'string') {
            return getText(acc, value as T)
        }
        const {text, ...itemOptions} = value
        if (typeof text === 'number' || typeof text === 'string') {
            return getText(acc, text as T, itemOptions)
        }
        return acc
    }, '')
}

/** Text 가 있다면 Text 앞에 빈칸을 채워주는 함수 */
export const prefixBlankText = createDecorateText({
    prefix: ' ',
})

/** Text 가 있다면 Text 를 () 로 감싸주는 함수 */
export const parenthesesText = createDecorateText({
    prefix: '(',
    postfix: ')',
})
